import { getOverlapCategory, VisitResourceWarnings, WARNING_CATEGORY_LABELS, WARNING_SEVERITIES, WarningCategory, WarningSeverityValue } from "./visit-resource-warnings";
import { VisitWarnings } from "./visit-warnings";

export type VisitOrResourceWarningLabel = {
  severity: WarningSeverityValue;
  category: string;
  warning: string;
};

// --- helpers ----

const SEVERITIES = WARNING_SEVERITIES;
const CATEGORIES = WARNING_CATEGORY_LABELS;

const CATEGORY_KEY_BY_FIELD = {
  // VisitResourceWarnings
  noProviderAssigned: "UNASSIGNED",
  shiftMissing: "SHIFT",
  gapCreatedAfterVisit: "GAP",
  gapCreatedBeforeVisit: "GAP",
  duration: "DURATION",
  excessiveDriveTimeBeforeVisit: "DRIVE",
  excessiveDriveTimeAfterVisit: "DRIVE",
  providerNotLicensed: "STATE_LICENSE",
  providerNotActive: "INACTIVE_PROVIDER",
  providerServiceLineMismatch: "SERVICE_LINE",

  // VisitWarnings
  teamMismatch: "TEAM",
  resourceRequirementProviderTypes: "REQUIREMENTS",
  clinicalConstraintViolations: "CLINICAL_CONSTRAINT",
} as const satisfies Record<string, WarningCategory>;

type BoundaryLike = { type?: string | null; label?: string | null } | null | undefined;

const boundaryLabel = (boundary: BoundaryLike): string => {
  const boundaryType = boundary?.type ?? "shift";
  if (!boundary?.label || boundary.label === boundaryType) return boundaryType;
  return `${boundaryType} (${boundary.label})`;
};

export const NO_PROVIDER_WARNING: VisitOrResourceWarningLabel = {
  category: "UNASSIGNED",
  severity: SEVERITIES.UNASSIGNED,
  warning: WARNING_CATEGORY_LABELS.UNASSIGNED,
} as const;

// -------- resource warnings --------

export const getVisitResourceWarningLabels = (
  visitResourceWarning?: VisitResourceWarnings | null
): VisitOrResourceWarningLabel[] => {
  if (!visitResourceWarning) return [];

  const previousBoundaryType = boundaryLabel(visitResourceWarning.previousBlocker ?? null);
  const nextBoundaryType = boundaryLabel(visitResourceWarning.nextBlocker ?? null);

  const labels: VisitOrResourceWarningLabel[] = [];

  if (visitResourceWarning.shiftMissing) {
    const k = CATEGORY_KEY_BY_FIELD.shiftMissing;
    labels.push({
      severity: SEVERITIES[k],
      category: CATEGORIES[k],
      warning: `The provider does not have a shift scheduled at this time.`,
    });
  }

  if (visitResourceWarning.providerNotActive) {
    const k = CATEGORY_KEY_BY_FIELD.providerNotActive;
    labels.push({
      severity: SEVERITIES[k],
      category: CATEGORIES[k],
      warning: `The provider is not active.`,
    });
  }

  if (visitResourceWarning.previousVisitDriveTimeOverlap) {
    const mins = visitResourceWarning.previousVisitDriveTimeOverlap;
    const k = getOverlapCategory(mins);
    labels.push({
      severity: SEVERITIES[k],
      category: CATEGORIES[k],
      warning: `Overlaps with the prior ${previousBoundaryType} by ${Math.round(mins)} minutes.`,
    });
  }

  if (visitResourceWarning.nextVisitDriveTimeOverlap) {
    const mins = visitResourceWarning.nextVisitDriveTimeOverlap;
    const k = getOverlapCategory(mins);
    labels.push({
      severity: SEVERITIES[k],
      category: CATEGORIES[k],
      warning: `Overlaps with the following ${nextBoundaryType} by ${Math.round(mins)} minutes.`,
    });
  }

  if (visitResourceWarning.startOfShiftDriveTimeOverlap) {
    const mins = visitResourceWarning.startOfShiftDriveTimeOverlap;
    const k = getOverlapCategory(mins);
    labels.push({
      severity: SEVERITIES[k],
      category: CATEGORIES[k],
      warning: `The provider will need to start ${Math.round(mins)} minutes before the start of their shift.`,
    });
  }

  if (visitResourceWarning.endOfShiftDriveTimeOverlap) {
    const mins = visitResourceWarning.endOfShiftDriveTimeOverlap;
    const k = getOverlapCategory(mins);
    labels.push({
      severity: SEVERITIES[k],
      category: CATEGORIES[k],
      warning: `The provider will need to keep working for ${Math.round(mins)} minutes past the end of their shift.`,
    });
  }

  if (visitResourceWarning.gapCreatedAfterVisit) {
    const k = CATEGORY_KEY_BY_FIELD.gapCreatedAfterVisit;
    labels.push({
      severity: SEVERITIES[k],
      category: CATEGORIES[k],
      warning: `Creates a ${Math.round(visitResourceWarning.gapCreatedAfterVisit)}-minute gap after the visit.`,
    });
  }

  if (visitResourceWarning.gapCreatedBeforeVisit) {
    const k = CATEGORY_KEY_BY_FIELD.gapCreatedBeforeVisit;
    labels.push({
      severity: SEVERITIES[k],
      category: CATEGORIES[k],
      warning: `Creates a ${Math.round(visitResourceWarning.gapCreatedBeforeVisit)}-minute gap before the visit.`,
    });
  }

  if (visitResourceWarning.duration) {
    const k = CATEGORY_KEY_BY_FIELD.duration;
    const d = visitResourceWarning.duration;
    labels.push({
      severity: SEVERITIES[k],
      category: CATEGORIES[k],
      warning: `Expected duration: ${d.expectedDurationInMinutes} minutes, Actual duration: ${d.actualDurationInMinutes} minutes.`,
    });
  }

  if (visitResourceWarning.excessiveDriveTimeBeforeVisit) {
    const k = CATEGORY_KEY_BY_FIELD.excessiveDriveTimeBeforeVisit;
    labels.push({
      severity: SEVERITIES[k],
      category: CATEGORIES[k],
      warning: `Requires a ${Math.round(visitResourceWarning.excessiveDriveTimeBeforeVisit)}-minute drive before the visit.`,
    });
  }

  if (visitResourceWarning.excessiveDriveTimeAfterVisit) {
    const k = CATEGORY_KEY_BY_FIELD.excessiveDriveTimeAfterVisit;
    labels.push({
      severity: SEVERITIES[k],
      category: CATEGORIES[k],
      warning: `Requires a ${Math.round(visitResourceWarning.excessiveDriveTimeAfterVisit)}-minute drive after the visit.`,
    });
  }

  if (visitResourceWarning.providerNotLicensed) {
    const k = CATEGORY_KEY_BY_FIELD.providerNotLicensed;
    labels.push({
      severity: SEVERITIES[k],
      category: CATEGORIES[k],
      warning: `Provider is not licensed in ${visitResourceWarning.providerNotLicensed}.`,
    });
  }

  if (visitResourceWarning.providerServiceLineMismatch) {
    const k = CATEGORY_KEY_BY_FIELD.providerServiceLineMismatch;
    labels.push({
      severity: SEVERITIES[k],
      category: CATEGORIES[k],
      warning: `Provider is not assigned to the ${visitResourceWarning.providerServiceLineMismatch} service line.`,
    });
  }

  return labels;
};
// -------- visit warnings --------

export const getVisitWarningLabels = (
  visitWarning: VisitWarnings,
  resources: { provider: {id: number, firstName: string, lastName: string} | null}[]
): VisitOrResourceWarningLabel[] => {
  if (!visitWarning) return [];

  const labels: VisitOrResourceWarningLabel[] = [];

  if (visitWarning.teamMismatch) {
    const k = CATEGORY_KEY_BY_FIELD.teamMismatch;
    labels.push({
      severity: SEVERITIES[k],
      category: CATEGORIES[k],
      warning: `Team mismatch between assigned providers.`,
    });
  }

  if (visitWarning.resourceRequirementProviderTypes) {
    const k = CATEGORY_KEY_BY_FIELD.resourceRequirementProviderTypes;
    const { expectedProviderTypes, actualProviderIds, extraProviderIds } =
      visitWarning.resourceRequirementProviderTypes;

    const matchedProviderIds = actualProviderIds.filter(Boolean);
    const totalProviderIds = matchedProviderIds.length + extraProviderIds.length;
    
    const extraProviderNames = extraProviderIds.map(id => {
      const provider = resources.find(res => res.provider?.id == id)?.provider
      if (!provider) return "Unknown provider"
      return `${provider.firstName} ${provider.lastName}`
    }).join(', ')

    let problem = '';

    if (totalProviderIds < expectedProviderTypes.length) {
      problem = "Fewer providers than required by visit."
    } else if (totalProviderIds > expectedProviderTypes.length) {
      problem = "Too many providers assigned to this visit."
    } else if (extraProviderIds.length) {
      problem = `Some providers don't match provider type requirements for this visit: ${extraProviderNames}.`
    }

    if (problem) {
      labels.push({
        severity: SEVERITIES[k],
        category: CATEGORIES[k],
        warning: problem,
      });
    }
  }

  if (visitWarning.clinicalConstraintViolations?.length) {
    const k = CATEGORY_KEY_BY_FIELD.clinicalConstraintViolations;
    visitWarning.clinicalConstraintViolations.forEach((problem) => {
      labels.push({
        severity: SEVERITIES[k],
        category: CATEGORIES[k],
        warning: problem,
      });
    });
  }


  return labels;
};
